import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, configAPI } from '@/lib/api';
import { authUtils } from '@/lib/auth';
import { toast } from 'sonner';
import { cacheService } from '@/services/cache.service';
import type { CreateConfigBody } from '@/types';

// Authentication hooks
export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authAPI.login(email, password),
    onSuccess: async (data: any) => {
      console.log('Login response data:', data);

      // Dismiss loading toast and show success immediately
      toast.dismiss('login-toast');
      toast.success('Login successful!');

      // Store token and user data immediately
      if (data.data) {
        authUtils.setToken(data.data.token);
        authUtils.setUserEmail(data.data.user.email);
      } else {
        // Fallback if structure is different
        authUtils.setToken(data.token);
        authUtils.setUserEmail(data.user.email);
      }

      // Navigate immediately - don't wait for cache
      navigate('/dashboard');

      // Set up cache service and load data in background (non-blocking)
      setTimeout(async () => {
        try {
          cacheService.setQueryClient(queryClient);
          cacheService.prePopulateCache(queryClient);
          await cacheService.loadUserDataOnLogin(queryClient);
          queryClient.invalidateQueries({ queryKey: ['user'] });
          console.log('Background cache loading completed');
        } catch (error) {
          console.error('Background cache loading failed:', error);
        }
      }, 100); // Small delay to ensure navigation starts first
    },
    onError: (error: any) => {
      console.error('Login failed:', error);
    },
  });
};

export const useRegister = () => {
  return useMutation({
    mutationFn: ({
      name,
      company,
      email,
      password,
    }: {
      name: string;
      company: string;
      email: string;
      password: string;
    }) => authAPI.register(name, company, email, password),
    onSuccess: (data: any) => {
      console.log('Registration successful:', data);
      toast.dismiss('register-toast');
      toast.success('Registration successful! Please log in to continue.');
      // Navigate to login page after successful registration
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    },
    onError: (error: any) => {
      console.error('Registration failed:', error);
      // Always dismiss the loading toast so the button is never stuck
      toast.dismiss('register-toast');
      const message =
        error.response?.data?.message ||
        error.message ||
        'Registration failed. Please try again.';
      toast.error(message);
    },
  });
};

export const useForgotPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ email }: { email: string }) => authAPI.forgotPassword(email),
    retry: false, // Disable automatic retries
    retryDelay: 0, // No retry delay
    onSuccess: (data: any) => {
      console.log('Forgot password response:', data);

      // Check if the request was successful (status 204 or 200)
      if (data?.status === 204 || data?.status === 200) {
        toast.success('Password reset email sent! Please check your inbox.');

        // Navigate to login page after successful email sent
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (data?.status === 404) {
        // Handle case where backend returns 404 in data
        toast.error('No user found with this email');
      } else {
        // Fallback success case
        toast.success('Password reset email sent! Please check your inbox.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    },
    onError: (error: any) => {
      console.error('Forgot password failed:', error);

      // Check if this is a 404 error (user not found)
      if (error.response?.status === 404) {
        toast.error('No user found with this email');
      } else {
        toast.error(
          error.response?.data?.message || 'Failed to send reset email'
        );
      }
    },
  });
};

// Configuration hooks
export const useConfigs = (isAuthenticated = true) => {
  return useQuery({
    queryKey: ['configs'],
    queryFn: async () => {
      const configs = await configAPI.getAll();
      // Return the structure that the UI expects
      return {
        data: {
          configs: configs,
          total: configs.length,
        },
      };
    },
    staleTime: Infinity, // Never stale while user is logged in - we control cache manually
    enabled: isAuthenticated, // Only run query if user is authenticated
  });
};

export const useConfig = (id: string) => {
  return useQuery({
    queryKey: ['config', id],
    queryFn: async () => {
      const config = await configAPI.getById(id);
      // Return the structure that UI expects
      return {
        data: { config },
      };
    },
    enabled: !!id,
    staleTime: Infinity, // Never stale - we control cache manually
  });
};

export const useCreateConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configData: CreateConfigBody) => configAPI.create(configData),
    onSuccess: (data) => {
      // Update cache service with new data
      const currentCache = cacheService.getCacheData();
      if (currentCache && data?.config) {
        const updatedCache = {
          ...currentCache,
          configs: [...currentCache.configs, data.config],
          lastUpdated: Date.now(),
        };
        cacheService.updateCache(['configs'], {
          data: {
            configs: updatedCache.configs,
            total: updatedCache.configs.length,
          },
        });
        console.log(
          'Cache updated with new config (with handshake status):',
          data.config.handshakeCompleted
        );

        // Update React Query directly instead of invalidating
        queryClient.setQueryData(['configs'], {
          data: {
            configs: updatedCache.configs,
            total: updatedCache.configs.length,
          },
        });
      }
    },
  });
};

export const useUpdateConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateConfigBody>;
    }) => configAPI.update(id, data),
    onSuccess: (response: any, variables) => {
      // Use the actual backend response instead of form data
      const updatedConfig = response?.data?.config;

      if (updatedConfig) {
        // Update cache service with the actual backend response
        cacheService.updateConfigInCache(updatedConfig);

        // Update React Query directly with backend response
        queryClient.setQueryData(['config', variables.id], {
          data: { config: updatedConfig },
        });

        // Also update the configs list
        const currentCache = cacheService.getCacheData();
        if (currentCache) {
          const updatedCache = {
            ...currentCache,
            configs: currentCache.configs.map((config) =>
              config._id === variables.id ? updatedConfig : config
            ),
            lastUpdated: Date.now(),
          };

          queryClient.setQueryData(['configs'], {
            data: {
              configs: updatedCache.configs,
              total: updatedCache.configs.length,
            },
          });
        }
      }

      // Toast is handled by the component to show specific messages
    },
    onError: (error: any) => {
      // Let the component handle validation errors specifically
      if (error.response?.data?.errors) {
        console.log(
          'useUpdateConfig: Validation error detected, letting component handle it'
        );
        // Don't show any toast for validation errors - component will handle them
        return;
      }

      // Let the component handle generic validation error message
      if (error.response?.data?.message === 'Validation error') {
        console.log(
          'useUpdateConfig: Generic validation error detected, letting component handle it'
        );
        // Don't show generic error toast for validation errors
        return;
      }

      console.log('useUpdateConfig: Handling non-validation error');
      const message =
        error.response?.data?.message || 'Failed to update configuration';
      toast.error(message);
    },
  });
};

export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({
      token,
      password,
      confirmPassword,
    }: {
      token: string;
      password: string;
      confirmPassword: string;
    }) => authAPI.resetPassword(token, password, confirmPassword),
    retry: false, // Disable automatic retries
    retryDelay: 0, // No retry delay
    onSuccess: (data: any) => {
      console.log('Reset password response:', data);

      // Check if the request was successful (status 200)
      if (data?.status === 200) {
        toast.success('Password reset successfully!');

        // Navigate to login page after successful reset
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        // Fallback success case
        toast.success('Password reset successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    },
    onError: (error: any) => {
      console.error('Reset password failed:', error);

      // Check if this is a 404 error (token not found/invalid)
      if (error.response?.status === 404) {
        toast.error(
          'Account not found. Please request a new password reset link.'
        );
      } else if (error.response?.status === 400) {
        const message = error.response?.data?.message || '';
        if (
          message.toLowerCase().includes('match') ||
          message.toLowerCase().includes('confirm')
        ) {
          toast.error(
            'Passwords do not match. Please make sure both fields are identical.'
          );
        } else if (
          message.toLowerCase().includes('expired') ||
          (message.toLowerCase().includes('invalid') &&
            message.toLowerCase().includes('token'))
        ) {
          toast.error(
            'Your password reset link has expired or is invalid. Please request a new one.'
          );
        } else if (message.toLowerCase().includes('password')) {
          toast.error(message);
        } else {
          toast.error(
            message ||
              'Invalid reset request. Please check your input and try again.'
          );
        }
      } else {
        toast.error(
          error.response?.data?.message || 'Failed to reset password'
        );
      }
    },
  });
};

export const useTriggerHandshake = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configId: string) => configAPI.triggerHandshake(configId),
    onSuccess: (result: any, configId: string) => {
      // Update the specific config in cache with new handshake status
      const currentConfig = cacheService.getConfigFromCache(configId);
      if (currentConfig && result?.config) {
        cacheService.updateConfigInCache(result.config);
      }

      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['config', configId] });
      queryClient.invalidateQueries({
        queryKey: ['handshake-status', configId],
      });
      queryClient.invalidateQueries({ queryKey: ['configs'] });

      // Check if handshake failed (backend returns 200 even on failure)
      if (result && result.handshake && !result.handshake.success) {
        const errorMessage = result.handshake.message || 'Handshake failed';
        toast.error(errorMessage);
      } else if (result && result.handshake && result.handshake.success) {
        toast.success('Handshake completed successfully');
      }
    },
    onError: (error: any) => {
      // Only show error for actual HTTP errors, not handshake failures
      // (handshake failures are handled in onSuccess due to 200 status)
      console.error('=== ONERROR HANDLER CALLED ===', error);

      // Try multiple ways to get error message
      let errorMessage =
        error.response?.data?.data?.handshake?.message ||
        error.response?.data?.message ||
        error.message ||
        error.code ||
        `Request failed: ${error.response?.status || 'Unknown'}`;

      // Provide more specific fallback based on status code
      if (error.response?.status === 401) {
        errorMessage =
          'Invalid Optimove API token. Please check your API credentials.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Configuration not found.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Optimove server error. Please try again later.';
      } else if (error.code === 'ENOTFOUND') {
        errorMessage = `Invalid Optimove Base URL: Cannot reach domain "${error.config?.url?.replace(/^https?:\/\//, '')}".`;
      }

      // Only use generic fallback if errorMessage is truly empty/null
      if (!errorMessage || errorMessage.trim() === '') {
        errorMessage = 'Failed to trigger handshake';
      }

      console.error('=== FINAL ERROR MESSAGE ===', errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useHandshakeStatus = (
  configId: string,
  handshakeCompleted?: boolean
) => {
  return useQuery({
    queryKey: ['handshake-status', configId],
    queryFn: () => configAPI.getHandshakeStatus(configId),
    enabled: !!configId,
    staleTime: 30 * 1000, // 30 seconds - handshake status changes frequently
    refetchInterval: handshakeCompleted ? false : 30 * 1000, // ← stop polling once done
  });
};

// Utility hook for authentication state
export const useAuth = () => {
  const queryClient = useQueryClient();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // Memoize so parent doesn't re-render children on every call
  const user = React.useMemo(
    () => ({
      email: authUtils.getUserEmail(),
    }),
    []
  );

  const logout = React.useCallback(() => {
    setIsLoggingOut(true);
    try {
      queryClient.cancelQueries();
      authUtils.logout();

      // Clear cache service
      cacheService.clearCache();

      queryClient.removeQueries();
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch {
      window.location.href = '/';
    }
  }, [queryClient]);

  return {
    isAuthenticated: authUtils.isAuthenticated(),
    user,
    logout,
    isLoggingOut,
  };
};

// Hook for API health check
export const useHealthCheck = () => {
  return useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const response = await fetch('/api/health');
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      return response.json();
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Check every minute
    retry: 3,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
